import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface NodePosition {
  x: number;
  y: number;
}

interface WorkflowNode {
  id: string;
  type: string;
  label: string;
  config: Record<string, unknown>;
  position: NodePosition;
}

interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
}

interface Workflow {
  id: string;
  name: string;
  description?: string;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  trigger_type: string;
  status: string;
  version: number;
  execution_count: number;
  last_executed_at?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface StepResult {
  node_id: string;
  node_type: string;
  label: string;
  status: 'completed' | 'failed' | 'skipped';
  output: unknown;
  error?: string;
  duration_ms: number;
}

interface ExecutionResult {
  execution_id: string;
  status: string;
  steps: StepResult[];
  output: unknown;
  error?: string;
  duration_ms: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const NODE_TYPES = [
  { value: 'ai_chat', label: 'AI Chat', description: 'Send a prompt to an AI model' },
  { value: 'ai_embed', label: 'AI Embed', description: 'Generate text embeddings' },
  { value: 'transform', label: 'Transform', description: 'Transform data with templates or JSON paths' },
  { value: 'condition', label: 'Condition', description: 'Branch based on a condition' },
  { value: 'output', label: 'Output', description: 'Collect the final result' },
] as const;

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  paused: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  archived: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

const TRIGGER_LABELS: Record<string, string> = {
  manual: 'Manual',
  schedule: 'Scheduled',
  webhook: 'Webhook',
};

// ---------------------------------------------------------------------------
// Node Editor sub-component
// ---------------------------------------------------------------------------

function NodeEditor({
  node,
  onUpdate,
  onRemove,
}: {
  node: WorkflowNode;
  onUpdate: (updated: WorkflowNode) => void;
  onRemove: () => void;
}) {
  const [configText, setConfigText] = useState(JSON.stringify(node.config, null, 2));
  const [configError, setConfigError] = useState('');

  const handleConfigChange = (value: string) => {
    setConfigText(value);
    try {
      const parsed = JSON.parse(value);
      setConfigError('');
      onUpdate({ ...node, config: parsed });
    } catch {
      setConfigError('Invalid JSON');
    }
  };

  return (
    <div className="rounded-md border bg-background p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-mono">
            {node.type}
          </span>
          <span className="text-xs text-muted-foreground font-mono">{node.id}</span>
        </div>
        <button
          onClick={onRemove}
          className="text-xs text-red-500 hover:text-red-700"
        >
          Remove
        </button>
      </div>
      <div className="space-y-3">
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Label</label>
          <input
            type="text"
            value={node.label}
            onChange={(e) => onUpdate({ ...node, label: e.target.value })}
            className="w-full border rounded-md px-3 py-1.5 bg-background text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">
            Config (JSON)
            {configError && <span className="ml-2 text-red-500">{configError}</span>}
          </label>
          <textarea
            value={configText}
            onChange={(e) => handleConfigChange(e.target.value)}
            rows={4}
            className={`w-full border rounded-md px-3 py-1.5 bg-background text-sm font-mono resize-none ${
              configError ? 'border-red-500' : ''
            }`}
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edge Editor sub-component
// ---------------------------------------------------------------------------

function EdgeEditor({
  edges,
  nodes,
  onUpdate,
}: {
  edges: WorkflowEdge[];
  nodes: WorkflowNode[];
  onUpdate: (edges: WorkflowEdge[]) => void;
}) {
  const [newSource, setNewSource] = useState('');
  const [newTarget, setNewTarget] = useState('');

  const addEdge = () => {
    if (!newSource || !newTarget || newSource === newTarget) return;
    // Prevent duplicates
    if (edges.some((e) => e.source === newSource && e.target === newTarget)) return;
    const edge: WorkflowEdge = {
      id: `e_${crypto.randomUUID().slice(0, 8)}`,
      source: newSource,
      target: newTarget,
    };
    onUpdate([...edges, edge]);
    setNewSource('');
    setNewTarget('');
  };

  const removeEdge = (edgeId: string) => {
    onUpdate(edges.filter((e) => e.id !== edgeId));
  };

  return (
    <div>
      <h4 className="text-sm font-medium mb-2">Connections</h4>
      {edges.length > 0 && (
        <div className="space-y-1 mb-3">
          {edges.map((edge) => {
            const sourceNode = nodes.find((n) => n.id === edge.source);
            const targetNode = nodes.find((n) => n.id === edge.target);
            return (
              <div key={edge.id} className="flex items-center justify-between text-sm rounded-md border px-3 py-1.5">
                <span className="text-foreground">
                  {sourceNode?.label ?? edge.source}{' '}
                  <span className="text-muted-foreground mx-1">-&gt;</span>{' '}
                  {targetNode?.label ?? edge.target}
                </span>
                <button
                  onClick={() => removeEdge(edge.id)}
                  className="text-xs text-red-500 hover:text-red-700"
                >
                  Remove
                </button>
              </div>
            );
          })}
        </div>
      )}
      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <label className="block text-xs text-muted-foreground mb-1">From</label>
          <select
            value={newSource}
            onChange={(e) => setNewSource(e.target.value)}
            className="w-full border rounded-md px-3 py-1.5 bg-background text-sm"
          >
            <option value="">Select source...</option>
            {nodes.map((n) => (
              <option key={n.id} value={n.id}>
                {n.label} ({n.type})
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <label className="block text-xs text-muted-foreground mb-1">To</label>
          <select
            value={newTarget}
            onChange={(e) => setNewTarget(e.target.value)}
            className="w-full border rounded-md px-3 py-1.5 bg-background text-sm"
          >
            <option value="">Select target...</option>
            {nodes.filter((n) => n.id !== newSource).map((n) => (
              <option key={n.id} value={n.id}>
                {n.label} ({n.type})
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={addEdge}
          disabled={!newSource || !newTarget || newSource === newTarget}
          className="px-3 py-1.5 rounded-md text-sm border hover:bg-muted disabled:opacity-50"
        >
          Add
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Execution Results sub-component
// ---------------------------------------------------------------------------

function ExecutionResults({ result }: { result: ExecutionResult }) {
  return (
    <div className="rounded-lg border bg-card p-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium">Execution Result</h4>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            result.status === 'completed'
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
          }`}>
            {result.status}
          </span>
          <span className="text-xs text-muted-foreground">{result.duration_ms}ms</span>
        </div>
      </div>

      {result.error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30 p-3 mb-3">
          <p className="text-sm text-red-700 dark:text-red-400">{result.error}</p>
        </div>
      )}

      {/* Steps */}
      <div className="space-y-2 mb-4">
        <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Steps</h5>
        {result.steps.map((step) => (
          <div key={step.node_id} className="rounded-md border px-3 py-2">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">{step.label}</span>
                <span className="text-xs text-muted-foreground font-mono">{step.node_type}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-1.5 py-0.5 rounded ${
                  step.status === 'completed'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : step.status === 'failed'
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
                }`}>
                  {step.status}
                </span>
                <span className="text-xs text-muted-foreground">{step.duration_ms}ms</span>
              </div>
            </div>
            {step.error && (
              <p className="text-xs text-red-500 mt-1">{step.error}</p>
            )}
            {step.output !== null && step.output !== undefined && (
              <pre className="text-xs text-muted-foreground mt-1 overflow-auto max-h-32 bg-muted rounded p-2">
                {typeof step.output === 'string' ? step.output : JSON.stringify(step.output, null, 2)}
              </pre>
            )}
          </div>
        ))}
      </div>

      {/* Final Output */}
      {result.output !== null && result.output !== undefined && (
        <div>
          <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Final Output</h5>
          <pre className="text-sm text-foreground bg-muted rounded-md p-3 overflow-auto max-h-48">
            {typeof result.output === 'string' ? result.output : JSON.stringify(result.output, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Workflows page
// ---------------------------------------------------------------------------

export function Workflows() {
  const queryClient = useQueryClient();
  const [view, setView] = useState<'list' | 'create' | 'edit'>('list');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Create/edit form state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [triggerType, setTriggerType] = useState('manual');
  const [nodes, setNodes] = useState<WorkflowNode[]>([]);
  const [edges, setEdges] = useState<WorkflowEdge[]>([]);

  // Execution state
  const [executionResult, setExecutionResult] = useState<ExecutionResult | null>(null);
  const [variablesText, setVariablesText] = useState('{}');

  // Node add state
  const [newNodeType, setNewNodeType] = useState('ai_chat');
  const [newNodeLabel, setNewNodeLabel] = useState('');

  // Query
  const queryParams = new URLSearchParams();
  if (search) queryParams.set('search', search);
  if (statusFilter) queryParams.set('status', statusFilter);

  const { data, isLoading } = useQuery({
    queryKey: ['workflows', search, statusFilter],
    queryFn: () => api<{ data: Workflow[] }>(`workflows?${queryParams.toString()}`),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (body: { name: string; description?: string; nodes: WorkflowNode[]; edges: WorkflowEdge[]; trigger_type: string }) =>
      api<{ data: Workflow }>('workflows', { body }),
    onSuccess: () => {
      resetForm();
      setView('list');
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api<{ data: Workflow }>(`workflows/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      resetForm();
      setView('list');
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      api(`workflows/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) =>
      api<{ data: Workflow }>(`workflows/${id}/duplicate`, { body: {} }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });

  const executeMutation = useMutation({
    mutationFn: ({ id, variables }: { id: string; variables: Record<string, unknown> }) =>
      api<{ data: ExecutionResult }>(`workflows/${id}/execute`, { body: { variables } }),
    onSuccess: (result) => {
      setExecutionResult(result.data);
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });

  // Helpers
  const resetForm = () => {
    setEditingId(null);
    setName('');
    setDescription('');
    setTriggerType('manual');
    setNodes([]);
    setEdges([]);
    setExecutionResult(null);
    setVariablesText('{}');
    setNewNodeType('ai_chat');
    setNewNodeLabel('');
  };

  const openCreate = () => {
    resetForm();
    setView('create');
  };

  const openEdit = (workflow: Workflow) => {
    setEditingId(workflow.id);
    setName(workflow.name);
    setDescription(workflow.description ?? '');
    setTriggerType(workflow.trigger_type);
    setNodes(workflow.nodes);
    setEdges(workflow.edges);
    setExecutionResult(null);
    setView('edit');
  };

  const addNode = () => {
    if (!newNodeLabel.trim()) return;
    const nodeId = `node_${crypto.randomUUID().slice(0, 8)}`;
    const newNode: WorkflowNode = {
      id: nodeId,
      type: newNodeType,
      label: newNodeLabel.trim(),
      config: getDefaultConfig(newNodeType),
      position: { x: 100 + nodes.length * 50, y: 100 + nodes.length * 80 },
    };
    setNodes([...nodes, newNode]);
    setNewNodeLabel('');
  };

  const updateNode = (index: number, updated: WorkflowNode) => {
    const copy = [...nodes];
    copy[index] = updated;
    setNodes(copy);
  };

  const removeNode = (index: number) => {
    const node = nodes[index];
    if (!node) return;
    const removedId = node.id;
    setNodes(nodes.filter((_, i) => i !== index));
    // Remove associated edges
    setEdges(edges.filter((e) => e.source !== removedId && e.target !== removedId));
  };

  const handleSave = () => {
    if (!name.trim() || nodes.length === 0) return;
    const payload = {
      name: name.trim(),
      description: description.trim() || undefined,
      nodes,
      edges,
      trigger_type: triggerType,
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, body: payload });
    } else {
      createMutation.mutate(payload as { name: string; description?: string; nodes: WorkflowNode[]; edges: WorkflowEdge[]; trigger_type: string });
    }
  };

  const handleExecute = () => {
    if (!editingId) return;
    let variables: Record<string, unknown> = {};
    try {
      variables = JSON.parse(variablesText);
    } catch {
      // ignore parse error, use empty
    }
    executeMutation.mutate({ id: editingId, variables });
  };

  const workflows = data?.data ?? [];
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const saveError = createMutation.error ?? updateMutation.error;

  // ---------------------------------------------------------------------------
  // Render: List view
  // ---------------------------------------------------------------------------

  if (view === 'list') {
    return (
      <div className="p-8 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-1">Workflows</h1>
            <p className="text-muted-foreground">
              Build and execute visual AI workflows by composing primitives.
            </p>
          </div>
          <button
            onClick={openCreate}
            className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium"
          >
            Create Workflow
          </button>
        </div>

        {/* Search and filters */}
        <div className="flex gap-4 mb-6">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search workflows..."
            className="flex-1 border rounded-md px-3 py-2 bg-background text-sm"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border rounded-md px-3 py-2 bg-background text-sm min-w-[140px]"
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {/* Workflow list */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />
            ))}
          </div>
        ) : workflows.length === 0 ? (
          <div className="rounded-lg border bg-card p-12 text-center text-muted-foreground">
            {search
              ? `No workflows found for "${search}"`
              : 'No workflows yet. Create one to get started.'}
          </div>
        ) : (
          <div className="space-y-3">
            {workflows.map((wf) => (
              <div
                key={wf.id}
                className="rounded-lg border bg-card p-5 hover:border-primary/30 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="cursor-pointer flex-1" onClick={() => openEdit(wf)}>
                    <h3 className="font-medium text-foreground">{wf.name}</h3>
                    {wf.description && (
                      <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">{wf.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-4">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                      {TRIGGER_LABELS[wf.trigger_type] ?? wf.trigger_type}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[wf.status] ?? STATUS_COLORS['draft']}`}>
                      {wf.status}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center gap-4">
                    <span>{wf.nodes.length} nodes</span>
                    <span>{wf.edges.length} connections</span>
                    <span>v{wf.version}</span>
                    <span>{wf.execution_count} runs</span>
                    {wf.last_executed_at && (
                      <span>Last run: {new Date(wf.last_executed_at).toLocaleDateString()}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEdit(wf)}
                      className="text-primary hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => duplicateMutation.mutate(wf.id)}
                      className="hover:underline"
                    >
                      Duplicate
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Archive "${wf.name}"?`)) {
                          deleteMutation.mutate(wf.id);
                        }
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      Archive
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: Create / Edit view
  // ---------------------------------------------------------------------------

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground mb-1">
            {editingId ? 'Edit Workflow' : 'Create Workflow'}
          </h1>
          <p className="text-muted-foreground text-sm">
            Define nodes and connections. The full visual canvas is coming soon.
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setView('list');
          }}
          className="px-4 py-2 rounded-md text-sm border hover:bg-muted"
        >
          Back to List
        </button>
      </div>

      {/* Workflow metadata */}
      <div className="rounded-lg border bg-card p-6 mb-6">
        <h3 className="text-sm font-medium mb-4">Workflow Details</h3>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="col-span-2">
            <label className="block text-sm text-muted-foreground mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My AI Workflow"
              className="w-full border rounded-md px-3 py-2 bg-background text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-muted-foreground mb-1">Trigger</label>
            <select
              value={triggerType}
              onChange={(e) => setTriggerType(e.target.value)}
              className="w-full border rounded-md px-3 py-2 bg-background text-sm"
            >
              <option value="manual">Manual</option>
              <option value="schedule">Scheduled</option>
              <option value="webhook">Webhook</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm text-muted-foreground mb-1">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What does this workflow do?"
            rows={2}
            className="w-full border rounded-md px-3 py-2 bg-background text-sm resize-none"
          />
        </div>
      </div>

      {/* Add node */}
      <div className="rounded-lg border bg-card p-6 mb-6">
        <h3 className="text-sm font-medium mb-4">Nodes</h3>
        <div className="flex gap-3 items-end mb-4">
          <div className="flex-1">
            <label className="block text-xs text-muted-foreground mb-1">Type</label>
            <select
              value={newNodeType}
              onChange={(e) => setNewNodeType(e.target.value)}
              className="w-full border rounded-md px-3 py-1.5 bg-background text-sm"
            >
              {NODE_TYPES.map((nt) => (
                <option key={nt.value} value={nt.value}>
                  {nt.label} -- {nt.description}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-xs text-muted-foreground mb-1">Label</label>
            <input
              type="text"
              value={newNodeLabel}
              onChange={(e) => setNewNodeLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addNode(); }}
              placeholder="e.g. Summarize Input"
              className="w-full border rounded-md px-3 py-1.5 bg-background text-sm"
            />
          </div>
          <button
            onClick={addNode}
            disabled={!newNodeLabel.trim()}
            className="px-4 py-1.5 rounded-md text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            Add Node
          </button>
        </div>

        {/* Node list */}
        {nodes.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            No nodes added yet. Add nodes above to build your workflow.
          </p>
        ) : (
          <div className="space-y-3">
            {nodes.map((node, i) => (
              <NodeEditor
                key={node.id}
                node={node}
                onUpdate={(updated) => updateNode(i, updated)}
                onRemove={() => removeNode(i)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Edges */}
      {nodes.length >= 2 && (
        <div className="rounded-lg border bg-card p-6 mb-6">
          <EdgeEditor
            edges={edges}
            nodes={nodes}
            onUpdate={setEdges}
          />
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={handleSave}
          disabled={!name.trim() || nodes.length === 0 || isSaving}
          className="bg-primary text-primary-foreground hover:bg-primary/90 px-5 py-2 rounded-md text-sm font-medium disabled:opacity-50"
        >
          {isSaving ? 'Saving...' : editingId ? 'Update Workflow' : 'Create Workflow'}
        </button>

        {editingId && (
          <button
            onClick={handleExecute}
            disabled={executeMutation.isPending || nodes.length === 0}
            className="bg-green-600 text-white hover:bg-green-700 px-5 py-2 rounded-md text-sm font-medium disabled:opacity-50"
          >
            {executeMutation.isPending ? 'Executing...' : 'Execute'}
          </button>
        )}

        <button
          onClick={() => {
            resetForm();
            setView('list');
          }}
          className="px-4 py-2 rounded-md text-sm border hover:bg-muted"
        >
          Cancel
        </button>
      </div>

      {/* Variables for execution */}
      {editingId && (
        <div className="rounded-lg border bg-card p-6 mb-4">
          <h3 className="text-sm font-medium mb-3">Execution Variables (JSON)</h3>
          <textarea
            value={variablesText}
            onChange={(e) => setVariablesText(e.target.value)}
            rows={3}
            className="w-full border rounded-md px-3 py-2 bg-background text-sm font-mono resize-none"
            placeholder='{"key": "value"}'
          />
        </div>
      )}

      {/* Error display */}
      {saveError && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30 p-3 mb-4">
          <p className="text-sm text-red-700 dark:text-red-400">
            {saveError instanceof Error ? saveError.message : 'An error occurred'}
          </p>
        </div>
      )}

      {executeMutation.isError && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30 p-3 mb-4">
          <p className="text-sm text-red-700 dark:text-red-400">
            {executeMutation.error instanceof Error ? executeMutation.error.message : 'Execution failed'}
          </p>
        </div>
      )}

      {/* Execution results */}
      {executionResult && <ExecutionResults result={executionResult} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Default config per node type
// ---------------------------------------------------------------------------

function getDefaultConfig(nodeType: string): Record<string, unknown> {
  switch (nodeType) {
    case 'ai_chat':
      return {
        system_prompt: 'You are a helpful assistant.',
        user_prompt: '{{input}}',
        model: 'gpt-4.1-mini',
        max_tokens: 2048,
        temperature: 0.7,
      };
    case 'ai_embed':
      return { model: 'text-embedding-3-small' };
    case 'transform':
      return { template: '{{input}}' };
    case 'condition':
      return { field: 'input', operator: 'exists', value: null };
    case 'output':
      return { output_key: 'result' };
    default:
      return {};
  }
}
